































/* General */
import Vue from 'vue'
import moment from 'moment'
import DefaultLayout from '@/layouts/default-layout.vue'
import MainContainer from '@/layouts/main-container.vue'

/* Mixins */

/* Components */
import CalendarSidebar from './views/calendar-sidebar.vue'
import CalendarMain from './views/calendar-main.vue'

/* Helpers */

/* Data types */

/* API */
import CalendarAPI from '@/api/modules/calendar.api'
import {
  ICalendarOutputFilterOptions,
  ICalendarOutputActivity,
  ICalendarOutputDeadline,
} from '@/api/types/response.types'

export default Vue.extend({
  name: 'Calendar',

  metaInfo: {
    title: 'Calendar',
  },

  components: {
    DefaultLayout,
    MainContainer,
    CalendarSidebar,
    CalendarMain,
  },

  data() {
    return {
      isLoading: true,
      isUpdatingData: true,
      filterOptions: {
        listCompanies: [],
        listEntities: [],
      } as ICalendarOutputFilterOptions,
      activities: null as ICalendarOutputActivity[],
      deadlines: null as ICalendarOutputDeadline[],
      daysWithDeadlines: [] as string[],
      firstDayOfCalendar: this.$route.query.date,
    }
  },

  computed: {
    entityID(): number {
      return this.$route.query.entity_id
        ? Number(this.$route.query.entity_id)
        : null
    },
    peopleIDsAsArray(): string[] {
      if (Array.isArray(this.$route.query.people_ids)) {
        return this.$route.query.people_ids
      } else if (this.$route.query.people_ids) {
        return this.$route.query.people_ids.split(',')
      } else {
        return []
      }
    },
    peopleIDsAsString(): string {
      if (Array.isArray(this.$route.query.people_ids)) {
        return this.$route.query.people_ids.join(',')
      }
      return this.$route.query.people_ids
    },
    date(): string {
      return this.$route.query.date
    },
    currentDate(): string {
      return this.$route.query.date
    },
    hasFilters(): boolean {
      return this.entityID !== null || this.hasSelectedPeople
    },
    hasSelectedPeople(): boolean {
      return this.peopleIDsAsArray.length > 0
    },
    filteringText(): string {
      if (!this.hasFilters) {
        return ''
      }
      if (
        this.filterOptions.listCompanies.length === 0 ||
        this.filterOptions.listEntities.length === 0
      ) {
        return ''
      }
      let result = 'Filtering by '

      if (this.entityID) {
        const entityName = this.filterOptions.listEntities
          .find(e => e.id === this.entityID)
          .name.replace('<', '&lt;')
          .replace('>', '&gt;')
        result += `<strong>${entityName}</strong>`

        if (this.hasSelectedPeople) {
          result += ' and '
        }
      }

      if (this.hasSelectedPeople) {
        if (this.peopleIDsAsArray.length === 1) {
          const userList = this.filterOptions.listCompanies.map(
            (element: any) => {
              const userFind = element.listPeople.find(
                (item: any) =>
                  item.id === parseInt(this.peopleIDsAsArray[0], 0),
              )
              return userFind
            },
          )
          result += `<strong>${
            userList.find((item: any) => item).name
          }</strong>`
        } else {
          result += `<strong>${this.peopleIDsAsArray.length} people</strong>`
        }
      }

      return result
    },
  },

  watch: {
    '$route.query': {
      immediate: true,
      async handler() {
        try {
          this.activities = null
          this.deadlines = null
          await Promise.all([
            this.hydrateDeadlines(),
            this.hydrateActivities(),
            this.updateDaysWithDeadlines(),
          ])
        } catch (error) {
          this.$error.emit(error)
        }
      },
    },
  },

  async created() {
    this.isLoading = true
    await this.hydrateFilterOptions()
    this.isLoading = false
  },

  methods: {
    async handleMonthChange(year: string, month: string) {
      this.firstDayOfCalendar = `${year}-${month}-1`
      await this.updateDaysWithDeadlines()
    },
    async updateDaysWithDeadlines() {
      const deadlineDatePickerDate = moment(this.firstDayOfCalendar).format(
        'YYYY-MM-DD',
      )
      /* Expand deadline range to cover weeks before and after cur month that are displayed on the cal */
      const startOfDeadlineRange = moment(deadlineDatePickerDate)
        .startOf('month')
        .add(-2, 'w')
        .format('YYYY-MM-DD')
      const endOfDeadlineRange = moment(deadlineDatePickerDate)
        .endOf('month')
        .add(2, 'w')
        .format('YYYY-MM-DD')

      await this.hydrateDaysWithDeadlines(
        startOfDeadlineRange,
        endOfDeadlineRange,
      )
    },
    async hydrateFilterOptions() {
      try {
        this.filterOptions = await CalendarAPI.get_FilterOptions()
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async hydrateDeadlines() {
      try {
        this.deadlines = await CalendarAPI.get_Deadlines(
          this.entityID,
          this.peopleIDsAsString,
          this.date,
        )
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async hydrateActivities() {
      try {
        this.activities = await CalendarAPI.get_Activities(
          this.entityID,
          this.peopleIDsAsString,
          this.date,
        )
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async hydrateDaysWithDeadlines(
      startOfDeadlineRange: string,
      endOfDeadlineRange: string,
    ) {
      try {
        const response = await CalendarAPI.get_DaysWithDeadlines(
          this.entityID,
          this.peopleIDsAsString,
          startOfDeadlineRange,
          endOfDeadlineRange,
        )
        this.daysWithDeadlines = response.daysWithDeadlines
      } catch (error) {
        this.$error.emit(error)
      }
    },
  },
})
